import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { AnalyticsEvent, AnalyticsEventProperties, AnalyticsUserProfile } from './analytics-events';
import { getAppPlatform, toMonth, toRobotCountBand } from './analytics-helpers';

let initialized = false;
let lastIdentity: string | null = null;
let currentPageName: string | null = null;

// User properties sent by earlier versions under camelCase names; removed on identify.
const LEGACY_USER_PROPERTIES = ['organizationId', 'role'];

export const initAnalytics = (apiKey: string, userId?: string) => {
  if (initialized) return;

  amplitude.init(apiKey, userId, {
    serverZone: 'EU',
    autocapture: {
      elementInteractions: false,
      pageViews: false,
      formInteractions: false,
      attribution: false,
      sessions: true,
      frustrationInteractions: true,
    },
  });

  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: 1,
  });
  amplitude.add(sessionReplayTracking);

  initialized = true;
};

export const isInitialized = () => {
  return initialized;
};

export const enableTracking = () => {
  amplitude.setOptOut(false);
};

/** Consent withdrawn: stop sending and clear the identifiers held in the browser. */
export const disableTracking = () => {
  lastIdentity = null;
  if (!initialized) return;
  amplitude.setOptOut(true);
  amplitude.reset();
};

/** Signed out: keep consent, but detach the next session from the previous user. */
export const resetAnalyticsUser = () => {
  lastIdentity = null;
  if (!initialized) return;
  amplitude.reset();
};

export const identifyUser = (profile: AnalyticsUserProfile) => {
  if (!initialized || profile.userId.length === 0) return;

  const properties = {
    user_role: profile.userRole,
    organization_id: profile.organizationId,
    organization_role: profile.organizationRole,
    organization_robot_count_band: toRobotCountBand(profile.organizationRobotCount),
    language: profile.language,
    login_variant: profile.loginVariant,
    two_factor_enabled: profile.isTwoFactorEnabled,
    account_created_month: toMonth(profile.accountCreatedAt),
    app_platform: getAppPlatform(),
  };

  // The user object in the store changes often; only send an identify when something changed.
  const identity = JSON.stringify({ userId: profile.userId, ...properties });
  if (identity === lastIdentity) return;
  lastIdentity = identity;

  if (amplitude.getUserId() !== profile.userId) {
    amplitude.setUserId(profile.userId);
  }

  const identify = new amplitude.Identify();
  LEGACY_USER_PROPERTIES.forEach((key) => identify.unset(key));
  Object.entries(properties).forEach(([key, value]) => {
    if (value == null) {
      identify.unset(key);
    } else {
      identify.set(key, value);
    }
  });
  amplitude.identify(identify);
};

export const setCurrentPageName = (pageName: string | null) => {
  currentPageName = pageName;
};

export const getCurrentPageName = () => currentPageName;

export const trackEvent = <E extends AnalyticsEvent>(
  eventName: E,
  eventProperties: AnalyticsEventProperties[E],
) => {
  if (!isInitialized()) return;
  amplitude.track(eventName, eventProperties);
};
